import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FiArrowRight, FiMapPin } from "react-icons/fi";
import { usePosts } from "../hooks/usePosts";
import PostCard from "../components/PostCard";
import styles from "./Home.module.css";

export default function Home() {
  const { posts, loading } = usePosts();
  const featured = posts[0];
  const recent = posts.slice(1, 4);
  const countries = Array.from(new Set(posts.map((p) => p.country)));

  if (loading || !featured) return null;

  return (
    <main>
      <section className={styles.hero}>
        <div className={styles.heroImageWrap}>
          <motion.img
            src={featured.coverImage}
            alt={featured.title}
            className={styles.heroImage}
            initial={{ scale: 1.1 }}
            animate={{ scale: 1 }}
            transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1] }}
          />
          <div className={styles.heroOverlay} />
        </div>

        <div className={styles.heroContent}>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className={styles.heroLabel}>Travel Blog & Stories</p>
            <h1 className={styles.heroTitle}>
              Wander.
              <br />
              <span className={styles.heroTitleAccent}>Discover.</span>
              <br />
              Share.
            </h1>
            <p className={styles.heroSubtitle}>
              Personal travel stories and experiences from around the world.
              <br />
              Follow the journey{" "}
              <a
                href="https://instagram.com/Travelwithavara"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.heroLink}
              >
                @TravelWithAvara
              </a>
            </p>
          </motion.div>

          <motion.div
            className={styles.heroCta}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
          >
            <Link to="/blog" className={styles.btnPrimary}>
              Explore Stories <FiArrowRight />
            </Link>
            <Link to="/about" className={styles.btnOutline}>
              About Me
            </Link>
          </motion.div>

          <motion.div
            className={styles.heroStats}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.9 }}
          >
            <div className={styles.stat}>
              <span className={styles.statNumber}>{countries.length}+</span>
              <span className={styles.statLabel}>Countries</span>
            </div>
            <div className={styles.statDivider} />
            <div className={styles.stat}>
              <span className={styles.statNumber}>{posts.length}</span>
              <span className={styles.statLabel}>Stories</span>
            </div>
            <div className={styles.statDivider} />
            <div className={styles.stat}>
              <span className={styles.statNumber}>&infin;</span>
              <span className={styles.statLabel}>Memories</span>
            </div>
          </motion.div>
        </div>

        <motion.div
          className={styles.scrollIndicator}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <span className={styles.scrollText}>Scroll</span>
            <div className={styles.scrollLine} />
          </motion.div>
        </motion.div>
      </section>

      <section className={styles.featuredSection}>
        <div className={styles.container}>
          <motion.div
            className={styles.sectionHeader}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <span className={styles.sectionLabel}>Featured</span>
            <h2 className={styles.sectionTitle}>Latest Story</h2>
          </motion.div>

          <motion.article
            className={styles.featuredCard}
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <Link to={`/blog/${featured.id}`} className={styles.featuredLink}>
              <div className={styles.featuredImage}>
                <img src={featured.coverImage} alt={featured.title} />
                <div className={styles.featuredImageOverlay} />
              </div>
              <div className={styles.featuredContent}>
                <div className={styles.featuredMeta}>
                  <span>
                    <FiMapPin size={14} /> {featured.location},{" "}
                    {featured.country}
                  </span>
                  <span>
                    {new Date(featured.date).toLocaleDateString("en-US", {
                      month: "long",
                      year: "numeric",
                    })}
                  </span>
                </div>
                <h3 className={styles.featuredTitle}>{featured.title}</h3>
                <p className={styles.featuredExcerpt}>{featured.excerpt}</p>
                <span className={styles.featuredReadMore}>
                  Read Full Story <FiArrowRight />
                </span>
              </div>
            </Link>
          </motion.article>
        </div>
      </section>

      <section className={styles.recentSection}>
        <div className={styles.container}>
          <motion.div
            className={styles.sectionHeader}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <span className={styles.sectionLabel}>Recent</span>
            <h2 className={styles.sectionTitle}>More Stories</h2>
          </motion.div>

          <div className={styles.grid}>
            {recent.map((post, i) => (
              <PostCard key={post.id} post={post} index={i} />
            ))}
          </div>

          <motion.div
            className={styles.viewAll}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <Link to="/blog" className={styles.btnOutline}>
              View All Stories <FiArrowRight />
            </Link>
          </motion.div>
        </div>
      </section>

      <section className={styles.quoteSection}>
        <div className={styles.container}>
          <motion.blockquote
            className={styles.quote}
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className={styles.quoteMark}>&ldquo;</span>
            <p>
              The world is a book, and those who do not travel read only one
              page.
            </p>
            <cite>Saint Augustine</cite>
          </motion.blockquote>
        </div>
      </section>
    </main>
  );
}
